import { Box } from '@mui/material';
import { useFormik } from 'formik';
import React, { useEffect, useState, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import * as yup from 'yup';

import { PreliminaryInfo, preliminaryInfoSchema } from '../../../../domain/person';
import { ErrorMessage } from '../../../../general/components/error-message';
import { CelticBankDisclaimer } from '../../../../general/components/footers/celtic-bank-disclaimer/CelticBankDisclaimer';
import { FormikPhoneField } from '../../../../general/inputs/formik-phone-field';
import { FormikTextField } from '../../../../general/inputs/formik-text-field';
import { RootState } from '../../../../store';
import { savePreliminaryInfo } from '../../../../store/reducers/preliminaryInfo';
import { removePhoneFormatting } from '../../../../utils';
import { DocumentConsentNotice } from '../../../legal-consent/components/document-consent-notice';
import { SubmitWorkflowButton } from '../../../workflow/components/submit-workflow-button';
import { setReferrerId } from '../../reducer/referrer';
import { requestVerification } from '../../reducer/session';

const selector = ({
  preliminaryInfo: { info },
  promotion: { promotion },
  session: { error, loading },
  referrer: { referrerId },
}: RootState) => {
  const phoneError =
    error && error.message === 'denied_invalid_line_type' ? 'Please enter a mobile phone number' : null;

  return {
    loading,
    error: phoneError ? null : error,
    phoneError,
    referrerId,
    personalDetails: info,
    promoCode: promotion?.promoCode,
  };
};

const validationSchemaApply = yup.object({ ...preliminaryInfoSchema });

const SetupAccountForm: FC = () => {
  const dispatch = useDispatch();
  const { personalDetails, phoneError, promoCode, error, loading, referrerId } = useSelector(selector);

  const { search } = useLocation();
  const formik = useFormik({
    initialValues: {
      ...personalDetails,
    },
    validationSchema: validationSchemaApply,
    onSubmit: (values: PreliminaryInfo) => {
      const phone = removePhoneFormatting(values.phone);
      dispatch(requestVerification({ ...values, promoCode, phone, referrerId, partnerId, embeddedSessionToken }));
      dispatch(savePreliminaryInfo({ ...values, phone }));
    },
  });
  const { setValues } = formik;

  const [partnerId, setPartnerId] = useState<string | undefined>();
  const [embeddedSessionToken, setEmbeddedSessionToken] = useState<string | undefined>();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const rrid = params.get('rrid');
    const email = params.get('email');
    const phone = params.get('phone');
    const partner = params.get('partnerID');
    const embeddedSession = params.get('session');

    if (partner) {
      setPartnerId(partner);
    }

    if (embeddedSession) {
      setEmbeddedSessionToken(embeddedSession);
    }

    if (rrid) {
      dispatch(setReferrerId(rrid));
    }

    if (email || phone) {
      const updatedValues = {
        email: email || '',
        phone: formatPhoneParam(phone),
      };
      dispatch(savePreliminaryInfo(updatedValues));
      void setValues(updatedValues);
    }
  }, [search, dispatch, setValues]);

  const [lastPhoneError, setLastPhoneError] = useState<string | null>(null);
  if (lastPhoneError !== phoneError) {
    formik.setFieldError('phone', phoneError || undefined);
    setLastPhoneError(phoneError);
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <ErrorMessage error={error} />
      <FormikTextField
        formik={formik}
        id="email"
        label="Work Email"
        name="email"
        type="email"
        sx={{ mt: error ? 2 : 0 }}
      />
      <FormikPhoneField formik={formik} fullWidth id="phone" label="Mobile Phone" name="phone" />
      <Box mt={4}>
        <DocumentConsentNotice />
      </Box>
      <SubmitWorkflowButton type="submit" sx={{ mt: 3 }} progress={loading} disableNote={true}>
        Next
      </SubmitWorkflowButton>
      <Box mt={3}>
        <CelticBankDisclaimer />
      </Box>
    </form>
  );
};

export { SetupAccountForm };

const formatPhoneParam = (phone: string | null) => {
  phone = phone || '';
  if (phone.length > 0 && !phone.startsWith('1')) {
    return removePhoneFormatting('1' + phone);
  }

  return removePhoneFormatting(phone);
};
